/**
 * Les barres de la voix, lues sur ce que LiveKit diffuse déjà : `isSpeaking` et `audioLevel` de chaque participant,
 * le nôtre compris, que le serveur calcule et renvoie à toute la salle (`SpeakersChanged`).
 *
 * Aucun analyseur audio : un analyseur par vignette ouvrait un `AudioContext` de plus chez chaque élève (mesuré sur
 * la box, 5 par page dans une salle pleine), pour une information que la salle reçoit déjà.
 *
 * Une seule horloge pour toutes les barres de la page : tant qu'une vignette écoute, toutes les 150 ms, chaque
 * participant qui parle reçoit une phase de l'ondulation (celle de l'ancienne vignette, `UsernameComponent`), plus
 * ample quand il parle fort ; celui qui se tait reçoit une seule fois des barres au repos.
 */
import { type Participant } from "livekit-client"

export const SPEAKING_BARS_EVERY_MS = 150
const PHASES = 12
const SILENT: readonly number[] = [0, 0, 0]
const BARS = [0, 1, 2] as const

/**
 * La force de la voix, de 0 à 1, sur `audioLevel` : une amplitude linéaire, où une voix normale donne quelques
 * centièmes et une voix forte quelques dixièmes. La racine écarte les voix basses ; une voix forte touche le haut.
 */
export function loudness(audioLevel: number): number {
    if (!Number.isFinite(audioLevel) || audioLevel <= 0) return 0
    return Math.min(1, Math.sqrt(audioLevel) * 2)
}

/**
 * Les trois barres d'une phase : une onde qui passe d'une barre à l'autre, autour d'un milieu et d'une amplitude
 * qui montent avec la force de la voix (de 0,2 à 0,5 pour une voix basse, de 0,3 à 1 pour une voix forte).
 */
export function speakingLevels(phase: number, strength: number): number[] {
    const middle = 0.35 + 0.3 * strength
    const swing = 0.15 + 0.2 * strength
    return BARS.map((index) => middle + swing * Math.sin((((phase + index * 2) % PHASES) * Math.PI) / 6))
}

type Listener = {
    participant: Pick<Participant, "isSpeaking" | "audioLevel">
    push: (levels: readonly number[]) => void
    quiet: boolean
}

const listeners = new Set<Listener>()
let timer: ReturnType<typeof setInterval> | undefined
let phase = 0

function tick() {
    phase = (phase + 1) % PHASES
    listeners.forEach((listener) => {
        if (listener.participant.isSpeaking) {
            listener.quiet = false
            listener.push(speakingLevels(phase, loudness(listener.participant.audioLevel)))
        } else if (!listener.quiet) {
            listener.quiet = true
            listener.push(SILENT)
        }
    })
}

/**
 * Pousse à `push` les barres d'un participant, au rythme de l'horloge commune, sans passer par l'état de React.
 * Rend de quoi arrêter : les barres retombent, et l'horloge s'arrête avec la dernière vignette.
 */
export function listenSpeakingBars(participant: Listener["participant"], push: Listener["push"]): () => void {
    const listener: Listener = { participant, push, quiet: true }
    listeners.add(listener)
    timer ??= setInterval(tick, SPEAKING_BARS_EVERY_MS)
    return () => {
        listeners.delete(listener)
        push(SILENT)
        if (listeners.size === 0 && timer != null) {
            clearInterval(timer)
            timer = undefined
        }
    }
}
